<script setup lang="ts">
import {
  useStoreAdditionalPhones,
  useStoreBonuses, useStoreIsSpendingBonuses,
  useStoreOrderComment, useStorePrice,
} from "~/composables/store/order/mainForm";
import {
  useStorePromoCode,
  useStoreSelectedService, useStoreSelectedServiceChildren,
} from "~/composables/store/order/serviceForm";
import {
  ORDER_AUTH_URL,
  ORDER_BCC_URL,
  ORDER_UN_AUTH_URL,
} from "~/constants/api/order/order";
import {useStoreServiceInputs} from "~/composables/store/services/service_inputs";
import type {InputField} from "~/constants/types/serviceInputs";
import BackDrop from "~/components/modal/BackDrop.vue";
import {useStoreDateTime} from "~/composables/store/order/datetime";
import SelectPaymentMethod from "~/components/component/Order/Payment/SelectPaymentMethod.vue";
import {
  PAYMENT_CARD,
  PAYMENT_CARD_KEY, PAYMENT_LEGAL_ENTITY,
} from "~/constants/values/payment/payment";
import type {SelectPaymentMethodInterface} from "~/constants/values/payment/payment";
import {validatePayment} from "~/composables/validations/paymentValidations";
import {useOrderData} from "~/composables/cookie/useOrderData";
import {useStoreSelectedAddress} from "~/composables/store/user/address/store";
import {clearCalculatorStore} from "~/composables/clearStore/order/clearCalculatorStore";
import {NAVIGATION_PRIVACY_POLICY} from "~/constants/navigation/navigation_routes";
import formatPrice from "~/utils/formatPrice";
import {getProfile} from "~/composables/api/profile/profile";
import userTokenCookie from "~/composables/cookie/userToken";

const props = defineProps({
  category_keyword: {
    type: String,
    default: ''
  },
  hide: Boolean,
  useBcc: Boolean,
})
const router = useLocaleRouter()
const {$toast, $toastError} = useNuxtApp()
const {t, locale} = useI18n()
const countCards = ref(0)
const service = useStoreSelectedService()
const price = useStorePrice()
const bonuses = useStoreBonuses()
const route = useRoute()
const loader = ref(false)
const orderComment = useStoreOrderComment()
const paymentError = ref('')
const inputStore: InputField[] = useStoreServiceInputs()
const isTermsAccepted = ref(false)
const isSpendingBonuses = useStoreIsSpendingBonuses()
const additional_numbers = useStoreAdditionalPhones()

const selectedPaymentMethod = ref<SelectPaymentMethodInterface>({
  method: PAYMENT_CARD,
  key: PAYMENT_CARD_KEY,
  value: null
})

const { setOrderData } = useOrderData()

const emit = defineEmits(['close-modal'])
const { profile } = useProfile()
const isTermsAcceptedFromAPI = computed(() => !!profile.value?.agreement_accepted_at)
const orderForm = useStoreOrderClientForm()
const tokenCookie = userTokenCookie()
const userToken = useStoreUserToken()
const auth = useStoreAuth()

getProfile({
  profile,
  orderForm,
  tokenCookie,
  userToken,
  auth
})
function closeModal() {
  emit('close-modal')
}


async function apiRequestOrder(url: string, data: any, orderData: any) {

  const {data: response, error, pending} = await useApi(url, 'POST', data)
  if (response && response.value) {
    // $toast(t('order.success'))
    setOrderData(orderData)
    if (response.value?.payment_url) {
      window.location.href = response.value.payment_url
    } else {
      router.push('/services/thanks')
    }

    clearCalculatorStore()
  }
  if (error && error.value) {
    console.log(error)
    if (error.value.statusCode === 422) {
      $toastError(error.value.data.message)
    } else if(error.value.statusCode === 406) {
      $toastError(error.value?.data?.error)
    } else if(error.value?.data?.message) {
      $toastError(error.value.data.message)
    } else if(error.value?.data?.error) {
      $toastError(error.value?.data?.error)
    } else {
      $toastError(t('Что то пошло не так'))
    }
  }
}

async function handleOrder() {
  if (!isTermsAccepted.value && !isTermsAcceptedFromAPI.value) {
    paymentError.value = t('errors.policy.user_agreement')
    return
  }
  loader.value = true
  const additionalServicesArray = Object.values(useStoreSelectedServiceChildren().value).map((service: any) => {
    return {
      id: service.service_id,
      count: service.count,
    };
  }) ?? [];
  const address = useStoreSelectedAddress().value
  if (!address){
    $toastError(t('errors.address.choose_one'))
    return
  }
  let data = <object>{
    service_id: service.value.service_id,
    address: {
      address_id: address.address_id,
      location_id: address.city?.location_id,
      street: address.street,
      house: address.house,
      block: address.block,
      flat: address.flat,
      floor: address.floor,
      entrance: address.entrance,
      complex: address.complex,
    },
    comment:orderComment.value,
    location_id: useStoreUserCity().value.location_id,
    additional_services: JSON.stringify(additionalServicesArray),
    order_type: useClientTypeStore().value,
    order_date: useStoreDateTime().value,
    promo_code: useStorePromoCode().value,
    bonus: isSpendingBonuses.value,
    agreement_accepted: isTermsAccepted.value,
    ...(additional_numbers.value.length > 0 && {
      additional_numbers: additional_numbers.value.map(e => ({number: removePhoneMask(e?.number)}))
    })
  }

  const orderData = {
    title: service.value.title,
    price: price.value,
  }

  if (!useStoreAuth().value) {
    if (selectedPaymentMethod.value?.method === PAYMENT_LEGAL_ENTITY && !selectedPaymentMethod.value?.value) {
      paymentError.value = t('legal_entity.form_error')
      loader.value = false
      return
    }
    data = {
      ...data,
      phone_number: useStoreOrderClientForm().value.phone_number.toString().replace(/\D/g, ''),
      full_name: useStoreOrderClientForm().value.full_name,
      payment_data: selectedPaymentMethod.value?.value,
      payment_type: selectedPaymentMethod.value?.method
    }
  } else {
    const validated = validatePayment(selectedPaymentMethod.value, countCards.value)
    if (!validated.validation) {
      loader.value = false
      paymentError.value = validated.message ?? ''
      return
    }
    const requestURL = useRequestURL();
    const baseUrl = `${requestURL.protocol}//${requestURL.host}`;
    data = {
      ...data,
      success_url: `${baseUrl}/services/thanks`,
      // fail_url: baseUrl + route.path + `?service_type=${route.query.service_type}&category_slug=${route.query.category_slug}`,
      fail_url: baseUrl + route.path,
      payment_type: selectedPaymentMethod.value.method,
      ...(selectedPaymentMethod.value?.options?.phone_number && {
        phone_number: selectedPaymentMethod.value.options.phone_number
      })
    }
    if (selectedPaymentMethod.value.key) {
      data = {
        ...data,
        [selectedPaymentMethod.value.key]: selectedPaymentMethod.value.value
      }
    }
  }
  const inputs = Object.values(inputStore.value).map((input: any) => ({
    input_field_id: input.input_field_id,
    value: input.default_value,
    field_name: input.field_name,
    option: input.option,
  }))
  data.input_fields = JSON.stringify(inputs)
  orderData.input_fields = JSON.parse(JSON.stringify(inputs))
  const url = props.useBcc ? ORDER_BCC_URL : (useStoreAuth().value ? ORDER_AUTH_URL : ORDER_UN_AUTH_URL)

  await apiRequestOrder(url, data, orderData)
  loader.value = false
  isSpendingBonuses.value = false
  paymentLegalEntityDataCookie().setLegalEntityData(null)
  additional_numbers.value = []
}
function updateCountCardsLength(value: number){
  countCards.value = value
}
function handleSelectPaymentMethod(payment: SelectPaymentMethodInterface) {
  selectedPaymentMethod.value = {
    method: payment.method,
    key: payment.key,
    value: payment.value,
    options: payment?.options
  }
  paymentError.value = ''
}


</script>

<template>
  <div class="order_section flex flex-col gap-3">
    <p class="h3"> 5. {{ $t('order_product.payment_method.title') }}</p>
    <template v-if="!hide">
      <SelectPaymentMethod :selectedPaymentMethod="selectedPaymentMethod"
                           :isAuth="useStoreAuth().value"
                           @updateCountCardsLength="updateCountCardsLength"
                           @selectPaymentMethod="handleSelectPaymentMethod"
                           @selectLegalEntity="handleSelectPaymentMethod"
      />
      <p v-if="paymentError" class="error-text">{{ paymentError }}</p>



      <div class="flex gap-2 w-fit items-center">

        <UICheckbox
            :key="bonuses"
            :disabled="!bonuses"
            v-model="isSpendingBonuses"
            id="spend_bonuses"
        />
        <label
            for="spend_bonuses"
            class="flex items-center"
            :class="{'text-secondary-700': !bonuses}"
        >
          <span class="text-sm">{{ $t('order_page.payment.spend_bonuses') }}</span>
          <span class="text-yellow-500 inline-flex gap-0 items-center font-semibold ml-1">
            <IconBonus class="w-4 h-4"/>
            {{formatPrice(bonuses)}}
          </span>
        </label>
<!--        <div-->
<!--            class="bg-yellow-400 p-1 rounded-full text-white font-semibold text-sm flex items-center gap-1 w-fit"-->
<!--        >-->
<!--          <IconBonus class="w-4 h-4"/>-->
<!--          {{formatPrice(bonuses)}}-->
<!--        </div>-->
      </div>
      <div v-if="!isTermsAcceptedFromAPI" class="flex gap-2 items-start">
        <UICheckbox
            v-model="isTermsAccepted"
            @change="paymentError = ''"
            id="accept_user_agreement"
        />

        <label for="accept_user_agreement" v-if="locale == 'ru'" class="text-sm">Я принимаю условия <NuxtLink :to="NAVIGATION_PRIVACY_POLICY" target="_blank" class="inline text-primary-green">Пользовательского соглашения</NuxtLink> <span class="text-red-500">*</span></label>
        <label for="accept_user_agreement" v-else-if="locale == 'kk'" class="text-sm">Мен <NuxtLink :to="NAVIGATION_PRIVACY_POLICY" target="_blank" class="inline text-primary-green">Пайдаланушы келісімінің</NuxtLink> шарттарын қабылдаймын <span class="text-red-500">*</span></label>
      </div>


      <UIBtn
          color="green"
          class="w-fit ml-auto flex flex-col"
          @click="handleOrder"
      >
        <span><span class="font-semibold">{{ $t('order_page.payment.button') }}</span> {{ formatPrice(price) }} ₸</span>
      </UIBtn>
    </template>
  </div>

<!--  <p v-if="useClientTypeStore().value===B2B" class="m-auto order-service-form-text mt-3">-->
<!--    {{ $t('main.price_service_difference') }}</p>-->
  <BackDrop :open-modal="loader"/>
  <UILoadingSpinner v-if="loader"
                    class="absolute  md:w-[10vw] md:h-[10vw]  w-[30vw] h-[30vw] z-100 top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%] text-green-500 "/>
</template>

<style scoped>
.date-color {
  color: #00B300;
  font-weight: 900;
}

.v-btn--active .date-color {
  color: #fff;
}

</style>